import * as com from "../../Common.js";
import { calculateDownPayAndLoanSize } from "./utils_loans";

const ensureSuccessfulResponse = async (
  response: Response,
  action: string
) => {
  if (response.ok) {
    return;
  }
  const detail = await response.text();
  throw new Error(
    `${action} failed (${response.status})${detail ? `: ${detail}` : ""}`
  );
};

export type PrequalLetterBrandingData = {
  logoMode?: "text" | "logo";
  companyLogo?: string;
  companyName?: string;
  companyTagline?: string;
  openingText?: string;
  loName?: string;
  loTitle?: string;
  loNmls?: string;
  loPhone?: string;
  loEmail?: string;
  coName?: string;
  coNmls?: string;
  coAddr1?: string;
  coAddr2?: string;
  branding?: {
    logoMode?: "text" | "logo";
    companyLogo?: string;
    companyName?: string;
    companyTagline?: string;
    openingText?: string;
    loName?: string;
    loTitle?: string;
    loNmls?: string;
    loPhone?: string;
    loEmail?: string;
    coName?: string;
    coNmls?: string;
    coAddr1?: string;
    coAddr2?: string;
  };
};

const createCommonInputBody = (prequalData: {
  salesPrice: string | number;
  ltv: number;
  loanID: string;
  downpayment: number;
  brandingData?: PrequalLetterBrandingData;
}): {
  price: string;
  loan: string;
  loanID: string;
} & PrequalLetterBrandingData => {
  const { salesPrice, ltv } = prequalData;
  const { loanSize } = calculateDownPayAndLoanSize(salesPrice, ltv);
  return {
    price: salesPrice.toString(),
    loan: prequalData.downpayment ? (Math.round(Number(salesPrice)) - Math.round(prequalData.downpayment)).toString() : loanSize.toString(),
    loanID: prequalData.loanID,
    ...(prequalData.brandingData || {}),
  };
};

/**
 * Generates a pre-qualification PDF blob and its corresponding download content based on the provided pre-qualification data.
 *
 * @param {Object} prequalData - An object containing pre-qualification data, including sales price, loan-to-value ratio, loan type, FHA status, borrower ID, APR, rates, term type, and PDF template.
 * @return {Promise<Object>} A promise resolving to an object containing the generated PDF blob and its corresponding download content.
 */
export const getPrequalPDFBlobAndContent = async (prequalData: {
  salesPrice: string | number;
  ltv: number;
  borrowerID: string;
  loanID: string;
  downpayment: number;
  brandingData?: PrequalLetterBrandingData;
}): Promise<{ blob: Blob; download: any }> => {
  let token = com.getUserToken();
  let disposition;

  const inputBodyForPDFGeneration = createCommonInputBody(prequalData);
  const response = await fetch("/borrower/getprequal_v2", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      Cache: "no-cache",
      "X-Borrower": prequalData.borrowerID,
      FromLO: "true",
    },
    body: JSON.stringify(inputBodyForPDFGeneration),
  });
  await ensureSuccessfulResponse(response, "Pre-qualification PDF download");
  disposition = (response.headers.get("Content-Disposition") || "").split("=");

  const download =
    disposition.slice(1).join().replace(/"/g, "") ||
    "prequalification.pdf";
  const responseBlob = await response.blob();
  const blob = responseBlob.slice(0, responseBlob.size, "application/pdf");
  return { blob, download };
};

/**
 * Generates a pre-qualification PDF blob for preview in iframe.
 */
export const getPrequalPDFBlobURL = async (prequalData: {
  salesPrice: string | number;
  ltv: number;
  borrowerID: string;
  loanID: string;
  downpayment: number;
  brandingData?: PrequalLetterBrandingData;
}): Promise<string> => {
  let token = com.getUserToken();

  const inputBodyForPDFGeneration = createCommonInputBody(prequalData);
  const response = await fetch("/borrower/getprequal_v2", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      Cache: "no-cache",
      "X-Borrower": prequalData.borrowerID,
      FromLO: "true",
    },
    body: JSON.stringify(inputBodyForPDFGeneration),
  });
  await ensureSuccessfulResponse(response, "Pre-qualification PDF preview");

  const blob = await response.blob();
  const blobURL = URL.createObjectURL(blob);
  return blobURL;
};

/**
 * Send the prequalification letter to the borrower, and save the pdf file
 * to db.
 */
export const sendPrequalPDFToBorrowerAndSaveToDB = async (prequalData: {
  salesPrice: string | number;
  ltv: number;
  borrowerID: string;
  email: string;
  loanID: string;
  borrowerName: string;
  downpayment: number;
  brandingData?: PrequalLetterBrandingData;
}): Promise<void> => {
  let token = com.getUserToken();

  const inputBodyForSendingPDF = {
    ...createCommonInputBody(prequalData),
    email: prequalData.email,
    name: prequalData.borrowerName,
  };
  const response = await fetch("/borrower/save_prequal", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + token,
      Cache: "no-cache",
      "X-Borrower": prequalData.borrowerID,
      FromLO: "true",
    },
    body: JSON.stringify(inputBodyForSendingPDF),
  });
  await ensureSuccessfulResponse(response, "Sending pre-qualification letter");
};
